import { Calendar, Clock, MapPin, UsersThree, ArrowUpRight } from "@phosphor-icons/react";

/**
 * Reusable event card. Renders identically on /legacy and /events by design —
 * only one card component so styling + data stays in one place.
 *
 * Uses the same red/black/white palette + typography (font-display + font-brush)
 * that appears throughout the Legacy page.
 */
export default function EventCard({ event }) {
  if (!event) return null;
  return (
    <article
      data-testid={`event-card-${event.id}`}
      className="border-2 border-[#B32027] bg-[#0F0F0F] p-6 md:p-10 grid gap-6 md:grid-cols-[1fr_auto] items-start"
    >
      <div className="space-y-4">
        <p className="text-[#B32027] uppercase tracking-[0.35em] text-[10px] font-bold flex items-center gap-2">
          <UsersThree size={12} weight="fill" /> {event.teamName}
        </p>
        <h3
          data-testid={`event-name-${event.id}`}
          className="font-display font-black uppercase tracking-tighter leading-[0.9] text-3xl md:text-5xl text-white"
        >
          {event.name}
        </h3>
        <dl className="grid sm:grid-cols-2 gap-3 text-white/85 text-sm md:text-base">
          <div className="flex items-start gap-2">
            <Calendar size={16} weight="fill" className="text-[#B32027] mt-0.5 shrink-0" />
            <div>
              <dt className="sr-only">Date</dt>
              <dd data-testid={`event-date-${event.id}`} className="leading-snug">{event.dateLabel}</dd>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <Clock size={16} weight="fill" className="text-[#B32027] mt-0.5 shrink-0" />
            <div>
              <dt className="sr-only">Time</dt>
              <dd data-testid={`event-time-${event.id}`} className="leading-snug">{event.time}</dd>
            </div>
          </div>
          <div className="flex items-start gap-2 sm:col-span-2">
            <MapPin size={16} weight="fill" className="text-[#B32027] mt-0.5 shrink-0" />
            <div>
              <dt className="sr-only">Location</dt>
              <dd className="leading-snug">
                <div data-testid={`event-location-${event.id}`} className="font-semibold text-white">{event.location}</div>
                <div data-testid={`event-address-${event.id}`} className="text-white/70">{event.address}</div>
              </dd>
            </div>
          </div>
        </dl>
      </div>
      <div className="flex md:flex-col gap-3 md:pt-2">
        <a
          href={event.registrationUrl}
          target="_blank"
          rel="noreferrer noopener"
          data-testid={`event-register-${event.id}`}
          className="inline-flex items-center justify-center gap-2 bg-[#B32027] hover:bg-[#D42730] text-white px-6 py-4 text-[11px] font-bold uppercase tracking-[0.3em] whitespace-nowrap transition-colors"
        >
          Join {event.teamName} <ArrowUpRight size={14} weight="bold" />
        </a>
      </div>
    </article>
  );
}
